import { Button, Modal, Segmented, Select, Skeleton, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  RELATED_RECORD_TYPES,
  type RelatedRecord,
  type RelatedRecordType
} from "../../Project/types/project-documents.types.ts"
import { useRelatedRecordOptions } from "../hooks/useRelatedRecordOptions.ts"

const { Text } = Typography

interface LinkRecordModalProps {
  projectId: string
  projectName: string
  currentRecord: RelatedRecord | null
  onClose: () => void
  onSubmit: (type: RelatedRecordType, id: string) => Promise<void>
  onUnlink: () => Promise<void>
}

export function LinkRecordModal({
  projectId,
  projectName,
  currentRecord,
  onClose,
  onSubmit,
  onUnlink
}: LinkRecordModalProps) {
  const { t } = useTranslation()
  const [recordType, setRecordType] = useState<RelatedRecordType>(
    currentRecord?.type ?? "project"
  )
  const [recordId, setRecordId] = useState<string | null>(
    currentRecord?.type === recordType ? currentRecord.id : null
  )
  const [saving, setSaving] = useState(false)
  const { options, loading, loadFailed } = useRelatedRecordOptions(
    recordType,
    projectId,
    projectName
  )

  useEffect(() => {
    setRecordId(
      recordType === "project"
        ? projectId
        : currentRecord?.type === recordType
          ? currentRecord.id
          : null
    )
    // `currentRecord` is read once per `recordType` change, not a dep.
  }, [recordType, projectId])

  const handleLink = async () => {
    if (!recordId) return
    setSaving(true)
    await onSubmit(recordType, recordId)
    setSaving(false)
  }

  const handleUnlink = async () => {
    setSaving(true)
    await onUnlink()
    setSaving(false)
  }

  return (
    <Modal
      open
      title={t("files.linkRecord.title")}
      onCancel={onClose}
      okText={t("files.linkRecord.confirm")}
      okButtonProps={{ disabled: !recordId, loading: saving }}
      onOk={() => void handleLink()}
      cancelButtonProps={{ disabled: saving }}
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          {currentRecord && (
            <Button
              type="link"
              danger
              className="float-left !px-0"
              disabled={saving}
              onClick={() => void handleUnlink()}
            >
              {t("files.linkRecord.unlink")}
            </Button>
          )}
          <CancelBtn />
          <OkBtn />
        </>
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Text type="secondary">{t("files.linkRecord.typeLabel")}</Text>
          <Segmented<RelatedRecordType>
            value={recordType}
            onChange={setRecordType}
            disabled={saving}
            options={RELATED_RECORD_TYPES.map((type) => ({
              label: t(`files.linkRecord.types.${type}`),
              value: type
            }))}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Text type="secondary">{t("files.linkRecord.recordLabel")}</Text>
          {recordType === "project" ? (
            <Text>{projectName}</Text>
          ) : loading ? (
            <Skeleton.Input active block />
          ) : (
            <Select<string>
              showSearch
              allowClear
              disabled={saving}
              value={recordId ?? undefined}
              onChange={(value) => setRecordId(value ?? null)}
              options={options}
              optionFilterProp="label"
              placeholder={t("files.linkRecord.searchPlaceholder")}
              notFoundContent={t(
                loadFailed ? "files.linkRecord.loadFailed" : "files.linkRecord.noRecords"
              )}
              className="w-full"
            />
          )}
        </div>
      </div>
    </Modal>
  )
}
